import { OperationVariables } from '@apollo/client';
import {
  Box,
  FormControlLabel,
  List,
  ListItem,
  Radio,
  Stack,
  Typography,
} from '@mui/material';

import { AsyncTypeaheadQuery } from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import { StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { COLORS } from '@/styles/tokens/colors';

import { FilterSearchInput } from './FilterSearchInput';
import { useAsyncFilterOptions } from './hooks/useAsyncFilterOptions';
import { AsyncFilterGroup, FilterOption } from './types';

interface AsyncFilterGroupDisplayProps<
  // Add constraint to Query generic
  Query extends AsyncTypeaheadQuery = AsyncTypeaheadQuery,
  Variables extends OperationVariables = OperationVariables,
> {
  group: AsyncFilterGroup<Query, Variables>;
  value: string[];
  onSelectAndClose: (optionValue: string) => void;
}

/**
 * Displays and handles filtering for an asynchronous list of filter options.
 */
export function AsyncFilterGroupDisplay<
  Query extends AsyncTypeaheadQuery = AsyncTypeaheadQuery,
  Variables extends OperationVariables = OperationVariables,
>({
  group,
  value: selectedValues,
  onSelectAndClose,
}: AsyncFilterGroupDisplayProps<Query, Variables>) {
  const { multi = true } = group;

  const { options, loading, inputText, onInputChange } = useAsyncFilterOptions(
    group,
    selectedValues
  );

  const handleSearchChange = (value: string) => {
    // Map simple onChange to hook's richer onInputChange
    onInputChange(value, 'input');
  };

  return (
    <Stack
      width={240}
      maxHeight={400}
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Always show search for async */}
      <FilterSearchInput
        value={inputText}
        onChange={handleSearchChange}
        placeholder="Search..."
        loading={loading}
      />

      <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
        {!loading && options.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body-xs">No results found</Typography>
          </Box>
        )}

        {options.length > 0 && (
          <List sx={{ px: 0.5, pb: 0.5, pt: 0 }}>
            {/* Added FilterOption type */}
            {options.map((option: FilterOption) => (
              <ListItem
                key={option.value}
                dense
                sx={{
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: COLORS.FUNCTIONAL.HOVER,
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectAndClose(option.value);
                  if (!multi) {
                    onInputChange('', 'reset');
                  }
                }}
              >
                <FormControlLabel
                  control={
                    multi ? (
                      <StyledCheckbox
                        checked={selectedValues.includes(option.value)}
                        size="small"
                        sx={{ m: '9px', width: 20, height: 20 }}
                      />
                    ) : (
                      <Radio
                        checked={selectedValues.includes(option.value)}
                        size="small"
                        disableRipple
                      />
                    )
                  }
                  label={
                    <Typography
                      variant="body-xs"
                      component="label"
                      color={COLORS.TEXT.PRIMARY}
                      sx={{
                        width: '100%',
                      }}
                      noWrap
                    >
                      {option.label}
                    </Typography>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Stack>
  );
}
